import React from 'react';
import { Tooltip } from 'antd';
import {
  CategoriesCell,
  PesticideGroupsCell,
  ChemicalFamiliesCell,
  RiskSymbolCell,
  ActionLinkCell,
  TagsCell,
  SimpleTextCell,
  ProductActiveSubstancesCell,
} from '@client/components/ui/table/TableCells';
import TruncatedText from '@client/components/ui/TruncatedText';
import { formatApprovalNumber } from '@client/utils/helpers';
import { buildLabelUrl } from '@client/utils/urlUtils';
import documentIcon from '../../../../img/client/icons/document.svg';

import {
  BiologicalLegend,
  ProductTypeLegend,
} from '@client/components/ui/productLegends';

/**
 * Factory function for expanded row rendering
 *
 * Creates a render function that displays additional record information
 * in an expandable row format using organized tag groups.
 *
 * @returns {Function} Expanded row render function
 *
 * @example
 * const expandedRowRender = createExpandedRowRender();
 * <Table expandable={{ expandedRowRender }} />
 */
// eslint-disable-next-line react/display-name
export const createExpandedRowRender = (expandedCultureIds) => (record) => {
  // eslint-disable-next-line no-undef
  const uniqueItems = new Map();

  const addUnique = (id, name) => {
    if (name && !uniqueItems.has(id)) {
      uniqueItems.set(id, name);
    }
  };

  record.activity_sectors?.forEach((sector) => {
    addUnique(sector.id, sector.name);
  });

  const cultures = record.cultures || [];
  // If expanded culture IDs are available, only show cultures that matched the filter
  const filteredCultures = expandedCultureIds?.length
    ? cultures.filter((c) => expandedCultureIds.includes(c.id))
    : cultures;

  filteredCultures.forEach((culture) => {
    addUnique(culture.id, culture.french_name);
  });

  if (uniqueItems.size === 0) return null;

  const tagGroups = Array.from(uniqueItems.entries())
    .slice(0, 20)
    .map(([id, name]) => ({
      key: id,
      name: name,
    }));

  return (
    <div className="expanded-row">
      <TagsCell
        items={tagGroups}
        getItemName={(item) => item.name}
        getItemKey={(item) => item.key}
      />
    </div>
  );
};

/**
 * Complete table columns configuration for active substances
 *
 * Defines all columns including their rendering, sorting behavior,
 * and display properties. Supports both server-side and client-side sorting.
 *
 * @param {Function} t - Internationalization function
 * @param {Object} currentSorting - Current sorting state
 * @param {string} currentSorting.field - Currently sorted field
 * @param {string} currentSorting.order - Current sort order ('ascend'|'descend')
 * @returns {Array} Complete Antd Table columns configuration
 *
 * @example
 * const columns = createTableColumns(t, { field: 'name', order: 'ascend' });
 * <Table columns={columns} />
 */
export const createTableColumns = (t, currentSorting) => [
  {
    title: t('product|table.Product name (Product type)'),
    dataIndex: 'name',
    key: 'name',
    render: (text, record) => {
      const href =
        record?.is_series && record?.series?.id
          ? `/series/${record.series.id}`
          : `/produits-commerciaux/details/${record.id}`;
      const productTypeNames = record?.product_types
        ?.map((pt) => pt?.name)
        .filter(Boolean)
        .join(', ');

      const hasPackagings = record?.packagings?.length > 0;
      const packagingNames = hasPackagings
        ? record.packagings.map((p) => p.name).filter(Boolean).join(', ')
        : '';

      return (
        <div className="flex flex-col gap-1 justify-start items-start">
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className={`text-[#105235] hover:text-[#105235] !font-[700] no-underline hover:underline text-[12px] ${record?.is_series ? 'normal-case' : ''}`}
            >
              <TruncatedText text={text} maxLength={35} />
            </a>
            {!record?.is_series && record?.approval_number && (
              <span className="text-black text-[11px]">
                ({formatApprovalNumber(record.approval_number)})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {productTypeNames && (
              <span className="text-black text-[11px]">
                ({productTypeNames})
              </span>
            )}
            {record?.is_organic_approval && <BiologicalLegend />}
            {record?.is_series && <ProductTypeLegend type="serie" />}
            {hasPackagings && (
              <Tooltip title={packagingNames}>
                <span><ProductTypeLegend type="emballage" /></span>
              </Tooltip>
            )}
          </div>
        </div>
      );
    },
    sorter: true,
    sortOrder: currentSorting?.field === 'name' ? currentSorting.order : null,
    width: 60,
  },
  {
    title: <div className="vertical-header-text">{t('product|Étiquette')}</div>,
    key: 'label',
    className: 'vertical-cell !align-middle',
    align: 'center',
    width: 40,
    render: (_, record) => {
      if (record?.is_series || !record?.approval_number)
        return <span style={{ color: '#8C8C8C' }}>-</span>;
      return (
        <Tooltip title={t('product|Étiquette')}>
          <button
            onClick={() =>
              window.open(
                buildLabelUrl(record.approval_number),
                '_blank',
                'noopener,noreferrer'
              )
            }
            className="flex items-center justify-center rounded-lg border border-[#D9D9D9] bg-white cursor-pointer transition-all hover:border-[#7CB305] hover:bg-[#F6FFED]"
            style={{
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              border: '1px solid #D9D9D9',
              background: '#fff',
            }}
          >
            <img
              src={documentIcon}
              alt="Étiquette"
              width="20"
              height="20"
              className="pointer-events-none"
            />
          </button>
        </Tooltip>
      );
    },
  },
  {
    title: (
      <div className="vertical-header-text">
        {t('product|Mélanges en cuve')}
      </div>
    ),
    className: 'vertical-cell !align-middle',
    dataIndex: 'tank_mixes',
    key: 'tank_mixes.name',
    render: (tank_mixes, record) =>
      record?.is_series ? null : <SimpleTextCell text={tank_mixes?.name} />,
    sorter: true,
    sortOrder:
      currentSorting?.field === 'tank_mixes.name' ? currentSorting.order : null,
    width: 40,
  },
  // {
  //   title: t('product|table.Commercial packagings'),
  //   className: 'vertical-cell !align-middle',
  //   dataIndex: 'packagings',
  //   key: 'packagings.name',
  //   render: (packagings) => <CategoriesCell categories={packagings} />,
  //   sorter: true,
  //   sortOrder:
  //     currentSorting?.field === 'packagings.name' ? currentSorting.order : null,
  //   width: 60,
  // },
  {
    title: (
      <div className="vertical-header-text">
        {t('product|table.Canadian distributor')}
      </div>
    ),
    key: 'canadian_distributor.title',
    dataIndex: 'canadian_distributor',
    className: 'vertical-cell !align-middle',
    render: (canadian_distributor, record) => {
      if (record?.is_series) return null;
      return (
        <TruncatedText text={canadian_distributor?.title} maxLength={40} />
      );
    },
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'canadian_distributor.title'
        ? currentSorting.order
        : null,
    width: 40,
  },
  {
    title: t('product|table.Active substance (Pesticide groups)'),
    dataIndex: 'active_substances',
    key: 'active_substances.active_substance.name',
    render: (active_substances) => (
      <ProductActiveSubstancesCell activeSubstances={active_substances} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'active_substances.active_substance.name'
        ? currentSorting.order
        : null,
    width: 60,
  },
  {
    title: t('product|table.Chemical family'),
    dataIndex: 'pesticide_group_associations',
    key: 'pesticide_group_associations.chemical_family.label',
    render: (associations) => (
      <ChemicalFamiliesCell associations={associations} />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field ===
      'pesticide_group_associations.chemical_family.label'
        ? currentSorting.order
        : null,
    width: 60,
  },
  {
    title: t('product|table.Activity type'),
    dataIndex: 'activity_type_associations',
    key: 'activity_type_associations.activity_type.label',
    render: (associations) => (
      <CategoriesCell
        categories={associations?.map((a) => a.activity_type).filter(Boolean)}
        label="label"
      />
    ),
    sorter: { compare: () => 0 },
    sortDirections: ['ascend', 'descend'],
    sortOrder:
      currentSorting?.field === 'activity_type_associations.activity_type.label'
        ? currentSorting.order
        : null,
    width: 60,
  },

  {
    title: (
      <div className="py-[10px]">{t('product|table.Risks on health')}</div>
    ),
    children: [
      {
        title: (
          <div className="vertical-header-text">
            {t('product|table.Toxicity aigue')}
          </div>
        ),
        key: 'acute_toxicity',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.health?.acute_toxicity_symbol}
            alt="Acute toxicity"
          />
        ),
        width: 10,
      },
      {
        title: (
          <div className="vertical-header-text">
            {t('product|table.Long term effects')}
          </div>
        ),
        key: 'long_term_effects',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.health?.long_term_effect_symbol}
            alt="Long term effects"
          />
        ),
        width: 10,
      },
    ],
  },
  {
    title: (
      <div className="py-[10px]">{t('product|table.Risks on environment')}</div>
    ),
    children: [
      {
        title: (
          <div className="vertical-header-text">
            {t('product|table.Fish or dophani')}
          </div>
        ),
        key: 'fish_daphnia',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.fishes_symbol}
            alt="Fish or daphnia"
          />
        ),
        width: 40,
      },
      {
        title: (
          <div className="vertical-header-text">{t('product|table.Birds')}</div>
        ),
        key: 'birds',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.birds_symbol}
            alt="Birds"
          />
        ),
        width: 40,
      },
      {
        title: (
          <div className="vertical-header-text">{t('product|table.Bees')}</div>
        ),
        key: 'bees',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.bees_symbol}
            alt="Bees"
          />
        ),
        width: 40,
      },
      {
        title: (
          <div className="vertical-header-text">
            {t('product|table.Persistance')}
          </div>
        ),
        key: 'persistence',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.persistence_symbol}
            alt="Persistence"
          />
        ),
        width: 40,
      },
      {
        title: (
          <div className="vertical-header-text">
            {t('product|table.Leaching potential')}
          </div>
        ),
        key: 'leaching_potential',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.leaching_symbol}
            alt="Leaching potential"
          />
        ),
        width: 40,
      },
    ],
  },
  {
    title: (
      <div className="vertical-header-text">
        {t('product|table.Product file')}
      </div>
    ),
    key: 'details',
    className: 'vertical-cell',
    render: (_, record) => {
      if (record?.is_series) return null;

      let isNouveau = false;
      if (record?.approval_status?.name === 'Actif' && record?.approval_start_date) {
        const startDate = new Date(record.approval_start_date);
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        isNouveau = startDate > oneYearAgo;
      }

      let isFin = false;
      if (record?.approval_end_date) {
        const endDate = new Date(record.approval_end_date);
        const now = new Date();
        const threeYearsFromNow = new Date();
        threeYearsFromNow.setFullYear(threeYearsFromNow.getFullYear() + 3);
        isFin = endDate > now && endDate <= threeYearsFromNow;
      }

      return (
        <div className="flex flex-col items-center gap-1" style={{ minWidth: 0 }}>
          <ActionLinkCell
            href={`/produits-commerciaux/details/${record.id}`}
            target="_blank"
            className="btn btn--primary btn--sm hover:text-inherit"
          >
            {t('product|table.See')}
          </ActionLinkCell>
          {isNouveau && (
            <Tooltip title={t('common|legends.nouveauTooltip')}>
              <span><ProductTypeLegend type="nouveau" height={14} /></span>
            </Tooltip>
          )}
          {isFin && (
            <Tooltip title={t('common|legends.finTooltip')}>
              <span><ProductTypeLegend type="fin" height={14} /></span>
            </Tooltip>
          )}
        </div>
      );
    },
    width: 40,
  },
];
